// src/ui/screens/post/ListingPage.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useLike } from '../../../utils/likeProvider';
import { useSave } from '../../../utils/saveProvider';
import { useSubscribe } from '../../../utils/subscribeProvider';
import { UserAvatar } from '../../../components/UserAvatar';
import { ListingItem } from './widgets/ListingItem';
import { db } from '../../../firebase';
import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  increment,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
// FIX: swapped the hand-rolled inline SVGs on the action bar for the same
// lucide-react icons VideoItem.jsx (and now ListingItem.jsx) use. Added
// ChevronLeft/ChevronRight for the desktop-only gallery arrows.
import { Heart, MessageCircle, Bookmark, Send, ChevronLeft, ChevronRight, Flag } from 'lucide-react';
// FIX: the comment button used to call navigate(`/listing/${listingId}/comments`),
// but that route was never registered in App.js — a real dead tap. Opening
// the actual CommentSection component instead, exactly the way
// VideoItem.jsx and ListingItem.jsx do.
import { CommentSection } from './widgets/CommentSection';
import FirebaseChatService from '../../../services/chat/firebaseChatService';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  cardLight: '#2C2F36',
};

// WhatsApp's official brand green.
const WHATSAPP_GREEN = '#25D366';

// Same helper as WantScreen — resolves buildConversationId whether it's an
// instance method, a static, or missing.
const buildConversationId = (a, b) => {
  const svc = FirebaseChatService;
  if (typeof svc?.buildConversationId === 'function') return svc.buildConversationId(a, b);
  if (typeof svc?.constructor?.buildConversationId === 'function') return svc.constructor.buildConversationId(a, b);
  return [a, b].sort().join('_');
};

// ── Helpers ─────────────────────────────────────────────────────────────────

const formatTime = (timestamp) => {
  if (!timestamp) return 'Just now';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const diff = Date.now() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
};

const formatPrice = (price) => {
  if (price === null || price === undefined || price === 'Negotiable') {
    return 'Negotiable';
  }
  if (typeof price === 'number') {
    return price.toLocaleString();
  }
  return price.toString();
};

const formatFieldLabel = (key) => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

// ── Sub-components ──────────────────────────────────────────────────────────

const StatChip = ({ icon, value, label, color = '#FFFFFF' }) => {
  return (
    <div className="flex-1 py-2.5 rounded-xl flex flex-col items-center" style={{ backgroundColor: COLORS.card }}>
      <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color }}>
        {icon === 'visibility' && (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        )}
        {icon === 'favorite' && (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        )}
        {icon === 'bookmark' && (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
        )}
        {icon === 'star' && (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        )}
      </svg>
      <span className="text-xs font-bold mt-1" style={{ color }}>{value}</span>
      <span className="text-[10px] text-white/35 truncate">{label}</span>
    </div>
  );
};

// FIX: previously rendered four inline <path> blocks by hand, picked via an
// `icon` string. Now maps that same string to a lucide-react component —
// matching VideoItem.jsx's SideButton — so the listing page's action bar
// uses the exact same icon set (and fill-when-active look) as the Atom
// video player and the feed cards.
const ACTION_BAR_ICONS = {
  favorite: Heart,
  chat: MessageCircle,
  bookmark: Bookmark,
  share: Send,
};

const ActionBtn = ({ icon, label, isActive, activeColor, onTap }) => {
  const IconComponent = ACTION_BAR_ICONS[icon];

  return (
    <button onClick={onTap} className="flex flex-col items-center px-2.5 py-2">
      {IconComponent && (
        <IconComponent
          size={22}
          color={isActive ? activeColor : 'rgba(255,255,255,0.55)'}
          fill={isActive ? activeColor : 'none'}
        />
      )}
      <span className="text-[11px] font-medium mt-0.5" style={{ color: isActive ? activeColor : 'rgba(255,255,255,0.45)' }}>
        {label}
      </span>
    </button>
  );
};

// FIX: added an optional `badge` prop so a button can carry a small pill tag
// (used by "Chat on Filatom" to flag it's Android-only for now), and added
// a 'whatsapp' icon option (drawn as a filled path, since the WhatsApp glyph
// isn't part of lucide-react) for the new "Chat on WhatsApp" button.
const ContactButton = ({ icon, label, color, onTap, badge }) => {
  return (
    <button
      onClick={onTap}
      className="relative w-full h-12 rounded-xl flex items-center justify-center gap-2.5"
      style={{ backgroundColor: color }}
    >
      {badge && (
        <span
          className="absolute -top-2 right-3 px-2 py-0.5 rounded-full text-[9px] font-bold text-white whitespace-nowrap"
          style={{ backgroundColor: 'rgba(0,0,0,0.7)', border: '1px solid rgba(255,255,255,0.25)' }}
        >
          {badge}
        </span>
      )}
      <svg
        className="w-5 h-5 text-white"
        fill={icon === 'whatsapp' ? 'currentColor' : 'none'}
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        {icon === 'chat' && (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        )}
        {icon === 'phone' && (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        )}
        {icon === 'whatsapp' && (
          <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.85.5 3.68 1.44 5.29L2 22l4.93-1.29c1.54.84 3.29 1.29 5.11 1.29h.01c5.46 0 9.9-4.45 9.9-9.91C21.95 6.45 17.5 2 12.04 2zm0 18.13h-.01a8.17 8.17 0 01-4.17-1.14l-.3-.18-3.1.81.83-3.02-.2-.31a8.14 8.14 0 01-1.26-4.38c0-4.5 3.67-8.17 8.19-8.17 2.19 0 4.24.85 5.79 2.4a8.13 8.13 0 012.4 5.78c0 4.51-3.67 8.18-8.17 8.18zm4.48-6.13c-.25-.12-1.47-.72-1.7-.8-.23-.08-.39-.12-.56.12-.17.25-.64.8-.78.96-.15.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.39-1.72-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.13-.15.17-.25.25-.42.08-.17.04-.31-.02-.43-.06-.12-.56-1.35-.77-1.85-.2-.48-.4-.42-.56-.42-.14 0-.31-.02-.47-.02-.17 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.88 2.4 1 2.57.12.17 1.75 2.67 4.25 3.74.59.26 1.06.41 1.42.52.6.19 1.14.16 1.57.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.15-1.18-.06-.1-.23-.16-.48-.28z" />
        )}
      </svg>
      <span className="text-white text-sm font-semibold">{label}</span>
    </button>
  );
};

const DetailRow = ({ label, value }) => {
  return (
    <div className="flex gap-4 py-2 border-b border-white/5 last:border-0">
      <span className="text-white/45 text-xs w-32 flex-shrink-0">{label}</span>
      <span className="text-white text-xs font-medium flex-1 break-words">{value}</span>
    </div>
  );
};

// ── Report Sheet ────────────────────────────────────────────────────────────

const ReportSheet = ({ listingId, onClose }) => {
  const { user } = useAuth();
  const [selectedReason, setSelectedReason] = useState(null);
  const [details, setDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const reasons = [
    { key: 'misleading', label: 'Misleading or Inaccurate Info', icon: 'info' },
    { key: 'fake', label: 'Fake or Fraudulent Listing', icon: 'warning' },
    { key: 'inappropriate', label: 'Inappropriate Content', icon: 'block' },
    { key: 'spam', label: 'Spam or Duplicate', icon: 'copy' },
    { key: 'wrong_category', label: 'Wrong Category', icon: 'category' },
    { key: 'already_sold', label: 'Already Sold / Unavailable', icon: 'sell' },
    { key: 'other', label: 'Other', icon: 'more' },
  ];

  const handleSubmit = async () => {
    if (!selectedReason || !user) return;
    setIsSubmitting(true);
    try {
      await setDoc(doc(collection(db, 'reports')), {
        listingId,
        reportedBy: user.uid,
        reason: selectedReason,
        details: details.trim(),
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
    } catch (error) {
      console.error('Report error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="p-6 flex flex-col items-center">
        <div className="w-16 h-16 rounded-full bg-green-500/15 flex items-center justify-center">
          <svg className="w-9 h-9 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-white text-lg font-bold mt-4">Report Submitted</h3>
        <p className="text-white/55 text-center text-xs mt-2">Thank you for keeping Filatom safe. Our team will review this within 24 hours.</p>
        <button onClick={onClose} className="w-full mt-6 py-3 rounded-xl text-white font-medium" style={{ backgroundColor: COLORS.cardLight }}>
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="p-5 pb-8">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-red-500/15">
          <svg className="w-[18px] h-[18px] text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="text-white text-lg font-bold">Report Listing</h3>
          <p className="text-white/40 text-xs">Help us understand what's wrong</p>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: COLORS.cardLight }}>
          <svg className="w-[18px] h-[18px] text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="h-px bg-white/5 mb-4" />

      {reasons.map((r) => {
        const isSelected = selectedReason === r.key;
        return (
          <button
            key={r.key}
            onClick={() => setSelectedReason(r.key)}
            className={`w-full flex items-center gap-3 p-3.5 rounded-xl mb-2 transition-all ${
              isSelected ? 'bg-red-500/15 border border-red-500/50' : 'border border-transparent'
            }`}
            style={{ backgroundColor: isSelected ? 'rgba(255,23,68,0.12)' : COLORS.cardLight }}
          >
            <span className={isSelected ? 'text-red-500' : 'text-white/40'}>
              <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {r.icon === 'info' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />}
                {r.icon === 'warning' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />}
                {r.icon === 'block' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />}
                {r.icon === 'copy' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />}
                {r.icon === 'category' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />}
                {r.icon === 'sell' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />}
                {r.icon === 'more' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />}
              </svg>
            </span>
            <span className={`flex-1 text-left text-xs ${isSelected ? 'text-white font-semibold' : 'text-white/70'}`}>
              {r.label}
            </span>
            {isSelected && (
              <svg className="w-[18px] h-[18px] text-red-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
            )}
          </button>
        );
      })}

      <textarea
        value={details}
        onChange={(e) => setDetails(e.target.value)}
        placeholder="Additional details (optional)…"
        rows={3}
        className="w-full mt-2 p-3.5 rounded-xl text-white text-xs placeholder:text-white/20 resize-none outline-none focus:ring-2 focus:ring-red-500/50"
        style={{ backgroundColor: COLORS.cardLight }}
      />

      <button
        onClick={handleSubmit}
        disabled={!selectedReason || isSubmitting}
        className={`w-full h-12 mt-4 rounded-xl text-white font-bold text-xs transition-all ${
          !selectedReason || isSubmitting
            ? 'opacity-25 cursor-not-allowed'
            : 'hover:opacity-90 active:scale-98'
        }`}
        style={{ backgroundColor: '#FF1744' }}
      >
        {isSubmitting ? (
          <div className="flex items-center justify-center gap-2">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span>Submitting...</span>
          </div>
        ) : (
          'Submit Report'
        )}
      </button>
    </div>
  );
};

// ── ListingPage ─────────────────────────────────────────────────────────────

export const ListingPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { isLiked, toggleLike } = useLike();
  const { isSaved, toggleSave } = useSave();
  const { isSubscribedTo, shouldSubBack, toggleSubscribe } = useSubscribe();

  const [listing, setListing] = useState(null);
  const [seller, setSeller] = useState(null);
  const [similarListings, setSimilarListings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSharing, setIsSharing] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [commentCount, setCommentCount] = useState(0);
  const [saveCount, setSaveCount] = useState(0);
  const [viewCount, setViewCount] = useState(0);
  const [averageRating, setAverageRating] = useState(0);
  const [ratingCount, setRatingCount] = useState(0);
  const [myRating, setMyRating] = useState(null);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [showReport, setShowReport] = useState(false);
  const [showCommentSection, setShowCommentSection] = useState(false);

  const listingId = id;

  // FIX: `viewCounted` used to be `useState`, but it was only ever read
  // inside the onSnapshot callback below — a closure created once when the
  // effect runs. Updating state doesn't recreate that closure, so the
  // callback kept reading the stale `false` it captured on mount forever.
  // Worse, incrementing the view count itself re-triggers this same
  // onSnapshot (it's listening to the doc we just wrote to), so the stale
  // check kept passing and it kept incrementing — a feedback loop, which is
  // exactly the "views balloon on one click" bug. A ref is mutated
  // synchronously and isn't subject to closure staleness, so it actually
  // gates the increment to once per listing.
  const viewCountedRef = useRef(false);
  // FIX: the seller sub-listener's `return () => sellerUnsub()` inside the
  // onSnapshot callback was never actually used for cleanup — Firestore's
  // SDK does nothing with an onSnapshot callback's return value. That meant
  // every time the listing doc changed, a brand new seller listener was
  // created without the previous one ever being canceled, silently stacking
  // up duplicate subscriptions. Tracking it in a ref lets us cancel the
  // previous one before creating the next.
  const sellerUnsubRef = useRef(null);

  // ── Fetch data ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!listingId) return;

    setIsLoading(true);
    viewCountedRef.current = false;

    // Listing listener
    const listingUnsub = onSnapshot(doc(db, 'listings', listingId), (docSnap) => {
      if (!docSnap.exists()) {
        setIsLoading(false);
        return;
      }
      const data = { id: docSnap.id, ...docSnap.data() };
      setListing(data);
      setLikeCount(data.likes || 0);
      setSaveCount((data.savedBy || []).length);
      setViewCount(data.viewCount || 0);
      setIsLoading(false);

      // Increment view count once per listing (guarded by the ref, not state)
      if (!viewCountedRef.current) {
        viewCountedRef.current = true;
        updateDoc(doc(db, 'listings', listingId), {
          viewCount: increment(1),
        }).catch(() => {});
      }

      // Fetch seller — cancel any previous listener before attaching a new one
      const ownerUid = data.uid || data.userRef;
      if (ownerUid) {
        sellerUnsubRef.current?.();
        sellerUnsubRef.current = onSnapshot(doc(db, 'users', ownerUid), (sellerSnap) => {
          if (sellerSnap.exists()) {
            setSeller({ id: sellerSnap.id, ...sellerSnap.data() });
          }
        });
      }
    }, (error) => {
      console.error('Listing fetch error:', error);
      setIsLoading(false);
    });

    // Comments count
    const commentsUnsub = onSnapshot(
      query(
        collection(db, 'listings', listingId, 'comments'),
        where('isReply', '==', false)
      ),
      (snap) => {
        setCommentCount(snap.size);
      }
    );

    // Ratings
    const ratingsUnsub = onSnapshot(
      collection(db, 'listings', listingId, 'ratings'),
      (snap) => {
        if (snap.empty) {
          setAverageRating(0);
          setRatingCount(0);
          setMyRating(null);
          return;
        }
        let total = 0;
        let myRatingVal = null;
        snap.forEach((d) => {
          total += d.data().rating || 0;
          if (d.id === user?.uid) {
            myRatingVal = d.data().rating;
          }
        });
        setRatingCount(snap.size);
        setAverageRating(total / snap.size);
        setMyRating(myRatingVal);
      }
    );

    return () => {
      listingUnsub();
      commentsUnsub();
      ratingsUnsub();
      sellerUnsubRef.current?.();
      sellerUnsubRef.current = null;
    };
  }, [listingId, user?.uid]);

  // Similar listings — depends on the listing's category, so it needs its
  // own effect keyed off that value. The previous version tried to read
  // `listing?.category` inside a setTimeout nested in the fetch effect
  // above, where `listing` was always the stale (pre-update) value from the
  // closure, and the timer/subscription were never cleaned up correctly.
  useEffect(() => {
    if (!listing?.category || !listingId) return;

    const similarUnsub = onSnapshot(
      query(
        collection(db, 'listings'),
        where('category', '==', listing.category),
        orderBy('createdAt', 'desc'),
        limit(7)
      ),
      (snap) => {
        const similar = snap.docs
          .filter(d => d.id !== listingId)
          .slice(0, 6)
          .map(d => ({ id: d.id, ...d.data() }));
        setSimilarListings(similar);
      }
    );

    return () => similarUnsub();
  }, [listing?.category, listingId]);

  // ── Media list ─────────────────────────────────────────────────────────────

  const mediaItems = React.useMemo(() => {
    if (!listing) return [];
    const items = [];
    const videoUrl = listing.videoUrl || '';
    if (videoUrl) {
      items.push({ type: 'video', url: videoUrl });
    }
    const imageUrls = (listing.imageUrls || []).filter(u => u);
    for (const url of imageUrls) {
      items.push({ type: 'image', url });
    }
    if (items.length === 0) {
      items.push({ type: 'placeholder', url: '' });
    }
    return items;
  }, [listing]);

  const videoThumbnailUrl = listing?.thumbnailUrl || (listing?.imageUrls?.[0] || '');

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleLike = async () => {
    if (!user) {
      alert('Sign in to like listings');
      return;
    }
    const ownerUid = listing?.uid || listing?.userRef || '';
    await toggleLike({
      listingId,
      listingOwnerId: ownerUid,
      listingTitle: listing?.title || 'your listing',
      thumbnailUrl: listing?.thumbnailUrl || listing?.imageUrls?.[0] || null,
      listingImageUrl: listing?.imageUrls?.[0] || null,
    });
    setLikeCount(prev => isLiked(listingId) ? prev - 1 : prev + 1);
  };

  const handleSave = async () => {
    if (!user) {
      alert('Sign in to save listings');
      return;
    }
    const ownerUid = listing?.uid || listing?.userRef || '';
    await toggleSave({
      listingId,
      listingOwnerId: ownerUid,
      listingTitle: listing?.title || 'your listing',
      thumbnailUrl: listing?.thumbnailUrl || listing?.imageUrls?.[0] || null,
      listingImageUrl: listing?.imageUrls?.[0] || null,
    });
    setSaveCount(prev => isSaved(listingId) ? prev - 1 : prev + 1);
  };

  const handleShare = async () => {
    setIsSharing(true);
    try {
      const title = listing?.title || 'this property';
      const url = `https://filatom.com/listing/${listingId}`;
      if (navigator.share) {
        await navigator.share({
          title: `Check out "${title}" on Filatom!`,
          text: `Check out "${title}" on Filatom!`,
          url: url,
        });
      } else {
        await navigator.clipboard.writeText(`Check out "${title}" on Filatom!\n${url}`);
        alert('Link copied to clipboard!');
      }
      await updateDoc(doc(db, 'listings', listingId), {
        shareCount: increment(1),
      });
    } catch (error) {
      console.error('Share error:', error);
    } finally {
      setIsSharing(false);
    }
  };

  const handleCall = () => {
    const phone = listing?.contactPhone;
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };

  // Opens the in-app chat screen with the listing's owner. The conversation
  // id is built from the two uids the same way the want viewer does it, so
  // both entry points land in the same thread.
  const handleChat = () => {
    if (!user) {
      alert('Sign in to start a chat');
      return;
    }
    const ownerUid = listing?.uid || listing?.userRef;
    if (!ownerUid) return;
    if (ownerUid === user.uid) return;

    const conversationId = buildConversationId(user.uid, ownerUid);

    navigate(`/chat/${conversationId}`, {
      state: {
        conversationId,
        receiverId: ownerUid,
        title: seller?.displayName || seller?.name || 'Chat',
        avatarUrl: seller?.profileImage || seller?.photoURL || null,
      },
    });
  };

  // New: WhatsApp fallback for reaching the seller directly, using the same
  // contact number as the Call button. Opens wa.me with a pre-filled
  // message referencing this listing's title, exactly like tapping a
  // WhatsApp deep link from any other app.
  const handleWhatsApp = () => {
    const phone = listing?.contactPhone;
    if (!phone) {
      alert('Seller phone number not available');
      return;
    }
    const digitsOnly = phone.toString().replace(/[^\d]/g, '');
    if (!digitsOnly) {
      alert('Seller phone number not available');
      return;
    }
    const title = listing?.title || 'your listing';
    const message = `Hey, reaching out from Filatom about the listing you posted of '${title}'.`;
    const waUrl = `https://wa.me/${digitsOnly}?text=${encodeURIComponent(message)}`;
    window.open(waUrl, '_blank', 'noopener,noreferrer');
  };

  // FIX: this used to call navigate(`/listing/${listingId}/comments`), but
  // that route was never registered in App.js — a dead tap with no console
  // error. Opens the real CommentSection component instead, exactly the
  // way VideoItem.jsx and ListingItem.jsx do.
  const handleShowComments = () => {
    if (!listingId) return;
    setShowCommentSection(true);
  };

  const handleViewImages = (initialIndex = 0) => {
    const imageUrls = (listing?.imageUrls || []).filter(u => u);
    if (imageUrls.length === 0) return;
    navigate(`/listing/${listingId}/images`, {
      state: {
        listingId,
        listingTitle: listing?.title || '',
        imageUrls,
        initialIndex,
      },
    });
  };

  const handleOpenAtomView = () => {
    if (!listing) return;
    // FIX: this used to call navigate('/atom-view', ...), but App.js only
    // registers the route as `/atom/:atomId` — "/atom-view" matched
    // nothing, so React Router silently rendered no route at all (no
    // console error, just a dead tap). Point it at the real path while
    // still passing the full listings array + initialIndex through router
    // state, mirroring how the Flutter version hands AtomViewScreen its
    // `listings`/`initialIndex` constructor props directly.
    const atomListings = [
      listing,
      ...similarListings.filter(l => l.videoUrl),
    ];
    navigate(`/atom/${listing.id}`, {
      state: {
        listings: atomListings,
        initialIndex: 0,
      },
    });
  };

  const handleSubscribe = async () => {
    if (!user) {
      alert('Sign in to subscribe');
      return;
    }
    const sellerUid = listing?.uid || listing?.userRef;
    if (sellerUid) {
      await toggleSubscribe(sellerUid);
    }
  };

  const handleSubmitRating = async (stars) => {
    if (!user) {
      alert('Sign in to rate this listing');
      return;
    }
    setIsSubmittingRating(true);
    try {
      await setDoc(doc(db, 'listings', listingId, 'ratings', user.uid), {
        rating: stars,
        uid: user.uid,
        createdAt: serverTimestamp(),
      });
      setMyRating(stars);
    } catch (error) {
      console.error('Rating error:', error);
      alert('Failed to submit rating');
    } finally {
      setIsSubmittingRating(false);
    }
  };

  const handleMediaPageChange = (index) => {
    setCurrentMediaIndex(index);
  };

  // Desktop-only prev/next handlers for the media banner. Clamped (not
  // wrapping) to match the existing dot-indicator behavior, which also
  // just jumps directly to an index rather than cycling.
  const goToPrevMedia = useCallback(() => {
    setCurrentMediaIndex((i) => Math.max(0, i - 1));
  }, []);

  const goToNextMedia = useCallback(() => {
    setCurrentMediaIndex((i) => Math.min(mediaItems.length - 1, i + 1));
  }, [mediaItems.length]);

  // ── Render helpers ────────────────────────────────────────────────────────

  const renderMediaHeader = () => {
    const total = mediaItems.length;
    const isFirst = currentMediaIndex === 0;
    const isLast = currentMediaIndex === total - 1;

    return (
      // Mobile: unchanged — full-bleed w-full h-[320px], no radius.
      // Desktop: contained inside the max-w page wrapper (see main render),
      // taller and rounded, matching a real listing-page gallery instead of
      // an edge-to-edge mobile strip.
      <div className="group relative w-full h-[320px] lg:h-[480px] lg:rounded-2xl bg-filatom-card overflow-hidden">
        <div
          className="flex transition-transform duration-300 ease-out h-full"
          style={{ transform: `translateX(-${currentMediaIndex * 100}%)` }}
        >
          {mediaItems.map((item, index) => (
            <div key={index} className="w-full h-full flex-shrink-0 relative">
              {item.type === 'video' && (
                <div
                  className="w-full h-full cursor-pointer relative"
                  onClick={handleOpenAtomView}
                >
                  <img
                    src={videoThumbnailUrl || item.url}
                    alt="Video thumbnail"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="absolute inset-0 bg-black/25 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-black/55 flex items-center justify-center border border-white/30">
                      <svg className="w-9 h-9 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                  <div className="absolute bottom-3 left-0 right-0 flex justify-center">
                    <div className="px-3 py-1 rounded-full bg-filatom-primary/85 flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                      <span className="text-white text-xs font-bold">Watch Atom</span>
                    </div>
                  </div>
                  <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/55 flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 text-filatom-primary" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                    <span className="text-filatom-primary text-[11px] font-bold">Atom</span>
                  </div>
                </div>
              )}
              {item.type === 'image' && (
                <div
                  className="w-full h-full cursor-pointer"
                  onClick={() => {
                    const videoOffset = mediaItems[0]?.type === 'video' ? 1 : 0;
                    handleViewImages(index - videoOffset);
                  }}
                >
                  <img
                    src={item.url}
                    alt={`Photo ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
              {item.type === 'placeholder' && (
                <div className="w-full h-full flex items-center justify-center bg-filatom-card">
                  <svg className="w-20 h-20 text-white/15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Gradient overlay */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-filatom-bg to-transparent pointer-events-none" />

        {/* Desktop-only prev/next arrows — clamped at the ends (no
            wraparound), matching the dot indicators below which also just
            jump to a specific index rather than cycling. Fade in on hover
            over the banner; mobile keeps swipe-only navigation exactly as
            before since these are `hidden` by default and only `lg:flex`. */}
        {total > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); goToPrevMedia(); }}
              disabled={isFirst}
              aria-label="Previous photo"
              className="hidden lg:flex absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center opacity-0 group-hover:opacity-100 disabled:opacity-0 transition-opacity duration-150"
              style={{ backgroundColor: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.15)' }}
            >
              <ChevronLeft size={20} color="#FFFFFF" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); goToNextMedia(); }}
              disabled={isLast}
              aria-label="Next photo"
              className="hidden lg:flex absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center opacity-0 group-hover:opacity-100 disabled:opacity-0 transition-opacity duration-150"
              style={{ backgroundColor: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.15)' }}
            >
              <ChevronRight size={20} color="#FFFFFF" />
            </button>
          </>
        )}

        {/* View all badge - only on images */}
        {mediaItems[currentMediaIndex]?.type === 'image' && (
          <button
            onClick={() => {
              const videoOffset = mediaItems[0]?.type === 'video' ? 1 : 0;
              handleViewImages(currentMediaIndex - videoOffset);
            }}
            className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/55 flex items-center gap-1.5 text-white text-[11px] font-semibold"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
            </svg>
            View all
          </button>
        )}

        {/* Media counter */}
        {total > 1 && (
          <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full bg-black/65 flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-white text-xs font-semibold">
              {currentMediaIndex + 1}/{total}
            </span>
          </div>
        )}

        {/* Dot indicators */}
        {total > 1 && (
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
            {mediaItems.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentMediaIndex(i)}
                className={`h-1.5 rounded-full transition-all duration-200 ${
                  i === currentMediaIndex
                    ? 'w-[18px] bg-filatom-primary'
                    : 'w-1.5 bg-white/40'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderPriceCard = () => {
    const isNegotiable = listing?.isNegotiable;
    const price = listing?.price;
    const priceText = isNegotiable || price === null || price === undefined
      ? 'Negotiable'
      : `UGX ${formatPrice(price)}`;

    const tags = [];
    const sf = listing?.specificFields || {};
    const purpose = sf.purpose || listing?.purpose || '';
    if (purpose) {
      const isRent = purpose.toLowerCase().includes('rent') || purpose.toLowerCase().includes('lease');
      tags.push({ label: purpose, color: isRent ? '#4DA3FF' : '#4CAF50' });
    }
    const freq = sf.paymentFrequency || listing?.paymentFrequency || '';
    if (freq) {
      tags.push({ label: freq, color: COLORS.orange });
    }

    return (
      <div className="relative">
        <div
          className="p-3.5 rounded-xl border"
          style={{
            backgroundColor: `${COLORS.orange}1A`,
            borderColor: `${COLORS.orange}59`,
          }}
        >
          <div className="flex items-center gap-3">
            <svg className="w-[22px] h-[22px] text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v1m0 7V14m0 1v1m0 0c1.11 0 2.08-.402 2.599-1M12 16v1m0-1v-1" />
            </svg>
            <span className="text-filatom-primary text-xl font-extrabold tracking-tight">
              {priceText}
            </span>
            {isNegotiable && (
              <span className="ml-auto px-2 py-0.5 rounded text-[11px] font-semibold" style={{ backgroundColor: `${COLORS.orange}26`, color: COLORS.orange }}>
                Negotiable
              </span>
            )}
          </div>
        </div>
        {tags.length > 0 && (
          <div className="absolute -top-2 right-3 flex gap-1.5">
            {tags.map((tag, i) => (
              <span
                key={i}
                className="px-2 py-0.5 rounded-full text-white text-[10px] font-bold tracking-wide"
                style={{ backgroundColor: tag.color, boxShadow: `0 2px 8px ${tag.color}59` }}
              >
                {tag.label}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderStatsRow = () => {
    return (
      <div className="flex gap-2">
        <StatChip icon="visibility" value={String(viewCount)} label="views" />
        <StatChip icon="favorite" value={String(likeCount)} label="likes" color="#FF1493" />
        <StatChip icon="bookmark" value={String(saveCount)} label="saves" color="#4DA3FF" />
        <StatChip icon="star" value={averageRating > 0 ? averageRating.toFixed(1) : '—'} label={`${ratingCount} ratings`} color="#FFD700" />
      </div>
    );
  };

  const renderActionBar = () => {
    const isLikedState = isLiked(listingId);
    const isSavedState = isSaved(listingId);

    return (
      <div className="py-1.5 rounded-xl flex justify-evenly" style={{ backgroundColor: COLORS.card }}>
        <ActionBtn
          icon="favorite"
          label={String(likeCount)}
          isActive={isLikedState}
          activeColor="#FF1493"
          onTap={handleLike}
        />
        <ActionBtn
          icon="chat"
          label={String(commentCount)}
          isActive={false}
          activeColor={COLORS.orange}
          onTap={handleShowComments}
        />
        <ActionBtn
          icon="bookmark"
          label={String(saveCount)}
          isActive={isSavedState}
          activeColor="#4DA3FF"
          onTap={handleSave}
        />
        {isSharing ? (
          <div className="flex items-center justify-center px-3">
            <div className="w-5 h-5 border-2 border-white/40 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <ActionBtn
            icon="share"
            label="Share"
            isActive={false}
            activeColor="#FFFFFF"
            onTap={handleShare}
          />
        )}
      </div>
    );
  };

  // "Chat on Filatom" now opens the in-app chat with the seller (see
  // handleChat above), and there's a WhatsApp button directly below
  // "Call Seller", styled in WhatsApp's brand green and using the same
  // contactPhone number as the call button.
  const renderContactButtons = () => {
    if (user?.uid === (listing?.uid || listing?.userRef)) return null;

    return (
      <div className="space-y-2.5">
        <ContactButton
          icon="chat"
          label="Chat on Filatom"
          color={COLORS.orange}
          onTap={handleChat}
        />
        <ContactButton icon="phone" label="Call Seller" color="#2196F3" onTap={handleCall} />
        <ContactButton
          icon="whatsapp"
          label="Chat on WhatsApp"
          color={WHATSAPP_GREEN}
          onTap={handleWhatsApp}
        />
      </div>
    );
  };

  const renderSellerCard = () => {
    if (!seller) return null;

    const isOwner = user?.uid === (listing?.uid || listing?.userRef);
    const sellerUid = seller.id || '';
    const sellerIsSubscribed = isSubscribedTo(sellerUid);
    const sellerShouldSubBack = shouldSubBack(sellerUid);
    const sellerName = seller.displayName || seller.name || 'Unknown';
    const sellerUsername = seller.username || '';
    const sellerAvatar = seller.profileImage || seller.photoURL || '';
    const verified = seller.verified || false;

    let subLabel;
    let subBg;
    if (sellerIsSubscribed) {
      subLabel = 'Subscribed';
      subBg = COLORS.card;
    } else if (sellerShouldSubBack) {
      subLabel = 'Sub Back';
      subBg = COLORS.orange;
    } else {
      subLabel = 'Subscribe';
      subBg = COLORS.orange;
    }

    return (
      <div className="p-4 rounded-xl" style={{ backgroundColor: COLORS.card }}>
        <div className="flex items-center gap-3.5">
          <button onClick={() => navigate(`/profile/${sellerUid}`)}>
            <UserAvatar size={48} imageUrl={sellerAvatar || null} name={sellerName} />
          </button>
          <div className="flex-1 min-w-0">
            <button onClick={() => navigate(`/profile/${sellerUid}`)} className="text-left">
              <div className="flex items-center gap-1.5">
                <span className="text-white text-sm font-semibold truncate">{sellerName}</span>
                {verified && (
                  <svg className="w-4 h-4 text-filatom-blue" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                )}
              </div>
              {sellerUsername && (
                <span className="text-white/45 text-xs">@{sellerUsername}</span>
              )}
            </button>
          </div>
          {!isOwner && sellerUid && (
            <button
              onClick={handleSubscribe}
              className={`px-3.5 py-2 rounded-full text-xs font-bold transition-all ${
                sellerIsSubscribed ? 'border border-white/15 text-white/70' : 'text-white'
              }`}
              style={{ backgroundColor: subBg }}
            >
              {subLabel}
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderRatingSection = () => {
    return (
      <div className="p-4 rounded-xl" style={{ backgroundColor: COLORS.card }}>
        <div className="flex items-center gap-3.5">
          <div className="w-[30px] h-[30px] rounded-lg flex items-center justify-center" style={{ backgroundColor: `${COLORS.orange}1F` }}>
            <svg className="w-4 h-4 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          </div>
          <span className="text-white text-sm font-bold">Rate This Listing</span>
        </div>
        <div className="h-px bg-white/5 my-3" />

        <div className="flex items-center gap-3.5">
          <span className="text-white text-4xl font-extrabold">
            {averageRating > 0 ? averageRating.toFixed(1) : '0.0'}
          </span>
          <div>
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((i) => {
                const filled = i <= Math.round(averageRating);
                return (
                  <span key={i} className={filled ? 'text-yellow-400' : 'text-white/20'}>
                    <svg className="w-[22px] h-[22px]" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </span>
                );
              })}
            </div>
            <span className="text-white/45 text-xs">{ratingCount} {ratingCount === 1 ? 'rating' : 'ratings'}</span>
          </div>
        </div>

        <div className="h-px bg-white/5 my-3" />

        <div className="text-white/55 text-xs font-medium mb-2">
          {myRating !== null ? `Your rating: ${myRating} ★` : 'Tap to rate this listing'}
        </div>
        {isSubmittingRating ? (
          <div className="flex items-center justify-center py-2">
            <div className="w-5 h-5 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="flex gap-1.5">
            {[1, 2, 3, 4, 5].map((star) => {
              const filled = myRating !== null && star <= myRating;
              return (
                <button
                  key={star}
                  onClick={() => handleSubmitRating(star)}
                  className={filled ? 'text-yellow-400' : 'text-white/35'}
                >
                  <svg className="w-9 h-9" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderPropertyDetails = () => {
    const rows = [];
    const addRow = (label, value) => {
      if (value) rows.push({ label, value });
    };

    addRow('Category', listing?.category);
    addRow('Sub-category', listing?.subcategory);
    addRow('Country', listing?.country);
    addRow('District', listing?.district);
    addRow('Division', listing?.division);

    if (rows.length === 0) return null;

    return (
      <div className="p-4 rounded-xl" style={{ backgroundColor: COLORS.card }}>
        <div className="flex items-center gap-3">
          <div className="w-[30px] h-[30px] rounded-lg flex items-center justify-center" style={{ backgroundColor: `${COLORS.orange}1F` }}>
            <svg className="w-4 h-4 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <span className="text-white text-sm font-bold">Property Info</span>
        </div>
        <div className="h-px bg-white/5 my-3" />
        {rows.map((row, i) => (
          <DetailRow key={i} label={row.label} value={row.value} />
        ))}
      </div>
    );
  };

  const renderSpecificFields = () => {
    const fields = listing?.specificFields || {};
    const entries = Object.entries(fields)
      .filter(([_, v]) => v && v.toString().trim())
      .sort((a, b) => a[0].localeCompare(b[0]));

    if (entries.length === 0) return null;

    return (
      <div className="p-4 rounded-xl" style={{ backgroundColor: COLORS.card }}>
        <div className="flex items-center gap-3">
          <div className="w-[30px] h-[30px] rounded-lg flex items-center justify-center" style={{ backgroundColor: `${COLORS.orange}1F` }}>
            <svg className="w-4 h-4 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
          </div>
          <span className="text-white text-sm font-bold">Listing Details</span>
        </div>
        <div className="h-px bg-white/5 my-3" />
        {entries.map(([key, value], i) => (
          <DetailRow key={i} label={formatFieldLabel(key)} value={String(value)} />
        ))}
      </div>
    );
  };

  // ── Main render ────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="min-h-screen bg-filatom-bg flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-filatom-bg flex items-center justify-center">
        <p className="text-white/50">Listing not found</p>
      </div>
    );
  }

  const isOwner = user?.uid === (listing.uid || listing.userRef);
  const categoryDisplay = listing.subcategory || listing.category || '';

  return (
    <div className="min-h-screen bg-filatom-bg">
      {/* Mobile back/report — unchanged, fixed floating circles over the
          edge-to-edge banner. Hidden on desktop in favor of the contained
          top bar below.
          NOTE on the back button: `navigate(-1)` here was never the bug —
          it's a correct, ordinary history-back call. The "always lands on
          Filatom instead of Posted" issue was PostScreen forgetting which
          tab it was on across a remount; that's fixed by syncing
          PostScreen's tab state to a `?tab=` URL param (see PostScreen.jsx),
          so this same navigate(-1) call now returns to the right tab. */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-20 flex items-center justify-between px-2 pt-2">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
        >
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          onClick={() => setShowReport(true)}
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
        >
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
          </svg>
        </button>
      </div>

      {/* DESKTOP LAYOUT NOTE: this wrapper is invisible on mobile (no
          default classes, only `lg:` ones), so mobile's edge-to-edge media
          banner and full-width content below render exactly as before. On
          desktop it becomes a centered, contained page: max-width column,
          side padding, and top padding to clear the fixed nav rail's
          content area plus give the gallery breathing room from the top
          edge. */}
      <div className="lg:max-w-6xl lg:mx-auto lg:px-10 lg:pt-8">

        {/* Desktop-only top bar — replaces the floating circles with a
            standard page header: a real "Back" link on the left, Report on
            the right. Sits above the (now contained, rounded) gallery
            instead of floating over it. */}
        <div className="hidden lg:flex items-center justify-between mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-white/60 hover:text-white text-sm font-medium transition-colors"
          >
            <ChevronLeft size={18} />
            Back to listings
          </button>
          <button
            onClick={() => setShowReport(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white/60 hover:text-white text-xs font-medium transition-colors"
            style={{ backgroundColor: COLORS.card }}
          >
            <Flag size={13} />
            Report
          </button>
        </div>

        {/* Media Header */}
        {renderMediaHeader()}

        {/* Content */}
        <div className="px-4 pb-8 -mt-1 lg:px-0 lg:mt-6 lg:pb-16">
          {/* Category & Time */}
          <div className="flex items-center gap-2 mt-3 lg:mt-0">
            {categoryDisplay && (
              <span
                className="px-2.5 py-1 rounded-full text-xs font-medium border"
                style={{
                  backgroundColor: `${COLORS.orange}1F`,
                  borderColor: `${COLORS.orange}4D`,
                  color: COLORS.orange,
                }}
              >
                {categoryDisplay}
              </span>
            )}
            <span className="flex-1" />
            <svg className="w-3.5 h-3.5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-white/40 text-xs">{formatTime(listing.createdAt)}</span>
          </div>

          {/* Title */}
          <h1 className="text-white text-xl lg:text-2xl font-extrabold leading-tight mt-2">
            {listing.title || 'No title'}
          </h1>

          {/* Location */}
          <div className="flex items-center gap-1.5 mt-2">
            <svg className="w-3.5 h-3.5 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-white/65 text-xs truncate">
              {[listing.division, listing.district, listing.country].filter(Boolean).join(', ')}
            </span>
          </div>

          {/* Address */}
          {listing.address && (
            <div className="flex items-center gap-1.5 mt-0.5">
              <svg className="w-3.5 h-3.5 text-white/35" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              </svg>
              <span className="text-white/45 text-xs truncate">{listing.address}</span>
            </div>
          )}

          {/* DESKTOP LAYOUT NOTE: on mobile this is just `flex flex-col
              gap-4` — a plain vertical stack, functionally identical to the
              old `space-y-4` and rendered in the exact same source order as
              before (sidebar-ish content first, then the rest), so mobile
              is pixel-for-pixel unchanged. On desktop it becomes a 3-column
              grid. The two children below are deliberately left in their
              original relative order in the JSX (Group A first, Group B
              second) — that's what keeps mobile's stacking order intact —
              and `lg:order-2` / `lg:order-1` is what flips Group A to the
              right-hand column visually on desktop, sticky, so price and
              contact buttons stay in view while the description and
              similar listings scroll underneath. */}
          <div className="mt-4 flex flex-col gap-4 lg:grid lg:grid-cols-3 lg:gap-8 lg:items-start">

            {/* Group A — price / stats / actions / contact.
                Desktop: right column, sticky. */}
            <div className="flex flex-col gap-4 lg:col-span-1 lg:order-2 lg:sticky lg:top-8">
              {renderPriceCard()}
              {renderStatsRow()}
              {renderActionBar()}
              {!isOwner && renderContactButtons()}
            </div>

            {/* Group B — description onward.
                Desktop: left column, normal scroll. */}
            <div className="flex flex-col gap-4 lg:col-span-2 lg:order-1">
              {listing.description && (
                <div className="p-4 rounded-xl" style={{ backgroundColor: COLORS.card }}>
                  <div className="flex items-center gap-3">
                    <div className="w-[30px] h-[30px] rounded-lg flex items-center justify-center" style={{ backgroundColor: `${COLORS.orange}1F` }}>
                      <svg className="w-4 h-4 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                    </div>
                    <span className="text-white text-sm font-bold">Description</span>
                  </div>
                  <div className="h-px bg-white/5 my-3" />
                  <p className="text-white/80 text-xs leading-relaxed whitespace-pre-wrap">
                    {listing.description}
                  </p>
                </div>
              )}

              {renderPropertyDetails()}
              {renderSpecificFields()}
              {renderRatingSection()}
              {renderSellerCard()}

              {/* Similar Listings */}
              {similarListings.length > 0 && (
                <div>
                  <div className="flex items-center gap-2.5 mb-3.5">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${COLORS.orange}1F` }}>
                      <svg className="w-[18px] h-[18px] text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                    </div>
                    <span className="text-white text-sm font-bold">AI-suggested recommendations</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 lg:gap-4">
                    {similarListings.map((item, index) => (
                      <ListingItem key={item.id || index} listing={item} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Report Modal */}
      {showReport && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60">
          <div className="w-full max-w-md bg-filatom-card rounded-t-2xl max-h-[90vh] overflow-y-auto">
            <ReportSheet listingId={listingId} onClose={() => setShowReport(false)} />
          </div>
        </div>
      )}

      {/* Comment Section */}
      {showCommentSection && listingId && (
        <CommentSection listingId={listingId} onClose={() => setShowCommentSection(false)} />
      )}
    </div>
  );
};

export default ListingPage;